import { Button, Modal, TextInput, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import {
  FinancialBlockConfig,
  createFinancialBlockConfig,
  updateFinancialBlockConfig,
} from '../api';
import { getRuleSentence } from '../ruleSentence';

const dictPrefix = 'financialPortal.settings.financialBlock';

interface FormValues {
  overdueDays: string;
  overdueDebtsCount: string;
}

interface Props {
  isOpen: boolean;
  config: FinancialBlockConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

// `raw` va tipado `any` por la convención del repo para los `validate` de
// react-hook-form (ver useValidations.ts): con varios campos, el tipo del
// valor se ensancha a la unión.
//
// El `required` incorporado de react-hook-form sólo rechaza valores falsy, y
// un espacio en blanco es truthy: pasa el `required` y llega aquí. Por eso se
// hace trim antes de convertir a número — sin el trim, `' '` se convierte en
// `Number(' ') === 0` y se guardaría en silencio como `0`.
function validateRange(min: number, max: number, message: string) {
  return (raw: any) => {
    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      return message;
    }
    const value = Number(trimmed);
    return (Number.isInteger(value) && value >= min && value <= max) || message;
  };
}

export function ConfigModal({ isOpen, config, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { control, handleSubmit, formState, reset } = useForm<FormValues>({
    defaultValues: { overdueDays: '', overdueDebtsCount: '' },
  });

  // El modal nunca se desmonta (sólo alterna `isOpen`), así que sin este reset
  // el estado del formulario sobrevive a cerrar y reabrir. Cubre además el
  // cambio de crear a editar: `config` entra en las dependencias.
  useEffect(() => {
    if (config) {
      reset({
        overdueDays: String(config.overdueDays),
        overdueDebtsCount: String(config.overdueDebtsCount),
      });
    } else {
      reset({ overdueDays: '', overdueDebtsCount: '' });
    }
  }, [isOpen, config, reset]);

  const values = useWatch({ control });

  // Frase viva: los dos campos son números sueltos y sólo la frase dice cómo
  // se combinan. Misma lógica que el panel de parámetros (ver
  // ruleSentence.ts), aquí con los valores en edición del formulario.
  const ruleSentence = useMemo(
    () =>
      getRuleSentence(
        t,
        values.overdueDays || '0',
        values.overdueDebtsCount || '0',
      ),
    [t, values.overdueDays, values.overdueDebtsCount],
  );

  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      const payload = {
        overdueDays: Number(formValues.overdueDays),
        overdueDebtsCount: Number(formValues.overdueDebtsCount),
      };

      const { error } = config
        ? await updateFinancialBlockConfig(payload)
        : await createFinancialBlockConfig(payload);

      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.saveError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.saveSuccess`),
      });
      onSaved();
    },
    [config, onSaved, t],
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <h1 className="text-dark fs-22 mb-4">{t(`${dictPrefix}.form.title`)}</h1>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} md={6}>
            <TextInput
              name="overdueDays"
              label={t(`${dictPrefix}.form.overdueDays`)}
              control={control}
              rules={{
                required: t(`${dictPrefix}.form.required`) as string,
                validate: validateRange(
                  1,
                  365,
                  t(`${dictPrefix}.form.daysRange`) as string,
                ),
              }}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              name="overdueDebtsCount"
              label={t(`${dictPrefix}.form.overdueDebtsCount`)}
              control={control}
              rules={{
                required: t(`${dictPrefix}.form.required`) as string,
                validate: validateRange(
                  1,
                  100,
                  t(`${dictPrefix}.form.debtsRange`) as string,
                ),
              }}
            />
          </Col>
        </Row>
        <p className="fs-14 text-muted mt-2 mb-0">{ruleSentence}</p>
        <Row className="mt-4">
          <Col xs={12} md={6}>
            <Button
              outlined
              fullwidth
              text={t(`${dictPrefix}.form.cancel`)}
              onClick={onClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              type="submit"
              fullwidth
              loading={formState.isSubmitting}
              text={t(`${dictPrefix}.form.save`)}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
